import React from 'react';
import { CarComparisonData, AnalysisSection, BreakdownScenario, BreakdownPeriod } from '../../types/carComparison';
import { getExpectedValueAtEnd, getSellingCosts, calculateResults, getMileageForYear } from '../../utils/carCalculations';
import { User } from '../../types/auth';
import { TylerState} from '../../utils/TylerState';

interface DetailedAnalysisProps {
  data: CarComparisonData;
  comparisonData?: CarComparisonData; // Optional: Car B in comparison mode
  activeAnalysisSection: AnalysisSection;
  setActiveAnalysisSection: (section: AnalysisSection) => void;
  selectedBreakdownScenario: BreakdownScenario;
  setSelectedBreakdownScenario: (scenario: BreakdownScenario) => void;
  selectedBreakdownPeriod: BreakdownPeriod;
  setSelectedBreakdownPeriod: (period: BreakdownPeriod) => void;
  formatCurrency: (value: number) => string;
  renderCostBreakdown: (scenario: BreakdownScenario, period: BreakdownPeriod, carData?: CarComparisonData) => React.ReactNode;
  generateProfessionalPDF: () => Promise<void>;
  user: User | null;
}

const DetailedAnalysis: React.FC<DetailedAnalysisProps> = ({
  data,
  comparisonData, // Car B in comparison mode
  activeAnalysisSection,
  setActiveAnalysisSection,
  selectedBreakdownScenario,
  setSelectedBreakdownScenario,
  selectedBreakdownPeriod,
  setSelectedBreakdownPeriod,
  formatCurrency,
  renderCostBreakdown,
  generateProfessionalPDF,
  user
}) => {
  const isComparisonMode = !!comparisonData;

  // Helper to calculate total kilometers driven during ownership
  const calculateTotalDrivenKm = (carData: CarComparisonData) => {
    if (!carData.useDetailedMileage) {
      return carData.annualMileage * carData.ownershipTimeframe;
    }

    let total = 0;
    for (let year = 1; year <= carData.ownershipTimeframe; year++) {
      total += getMileageForYear(carData, year);
    }
    return total;
  };

  // Helper to calculate final odometer reading (opening + driven)
  const calculateFinalOdometer = (carData: CarComparisonData) => {
    return (carData.openingMileage || 0) + calculateTotalDrivenKm(carData);
  };

  // Wrapper function to handle section changes with auto-scroll
  const handleAnalysisSectionChange = (section: AnalysisSection) => {
    setActiveAnalysisSection(section);
    // Auto-scroll to top on section change
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };
  return (
    <>
      {/* Detailed Analysis Container */}
      {/*
      <div className="detailed-analysis-container">
        <h4>Detailed Analysis</h4>
        <div className="analysis-cards-grid">
          <div className="analysis-card">
            <div className="card-icon">📊</div>
            <h5>Detailed Cash Flows</h5>
            <p>Year-by-year cash flow breakdown</p>
            <button 
              className="card-button"
              onClick={() => handleAnalysisSectionChange(activeAnalysisSection === 'cash-flows' ? null : 'cash-flows')}
            >
              {activeAnalysisSection === 'cash-flows' ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          <div className="analysis-card">
            <div className="card-icon">💰</div>
            <h5>Cost of Ownership</h5>
            <p>Detailed cost breakdown by category</p>
            <button 
              className="card-button"
              onClick={() => handleAnalysisSectionChange(activeAnalysisSection === 'cost-of-ownership' ? null : 'cost-of-ownership')}
            >
              {activeAnalysisSection === 'cost-of-ownership' ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          <div className="analysis-card">
            <div className="card-icon">🚗</div>
            <h5>Resale Value</h5>
            <p>Resale value and selling costs</p>
            <button 
              className="card-button"
              onClick={() => handleAnalysisSectionChange(activeAnalysisSection === 'resale-value' ? null : 'resale-value')}
            >
              {activeAnalysisSection === 'resale-value' ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          <div className="analysis-card">
            <div className="card-icon">📋</div>
            <h5>Lease Buyout</h5>
            <p>Lease residual value and buyout costs</p>
            <button 
              className="card-button"
              onClick={() => handleAnalysisSectionChange(activeAnalysisSection === 'lease-buyout' ? null : 'lease-buyout')}
            >
              {activeAnalysisSection === 'lease-buyout' ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          <div className="analysis-card">
            <div className="card-icon">📊</div>
            <h5>Annual Cost Graph</h5>
            <p>Year-by-year total cost breakdown</p>
            <button 
              className="card-button"
              onClick={() => handleAnalysisSectionChange(activeAnalysisSection === 'annual-cost-graph' ? null : 'annual-cost-graph')}
            >
              {activeAnalysisSection === 'annual-cost-graph' ? 'Hide Details' : 'Show Details'}
            </button>
          </div>

          <div className="analysis-card">
            <div className="card-icon">📄</div>
            <h5>Print to PDF</h5>
            <p>Generate professional PDF report</p>
            <button 
              className="card-button print-btn"
              onClick={generateProfessionalPDF}
            >
              📊 Print to PDF
            </button>
          </div>
        </div>
      </div>
      */}

      {/* Resale Value Breakdown */}
      {activeAnalysisSection === 'resale-value' && (
        <div className="resale-breakdown">
          {isComparisonMode ? (
            <>
              <h4>Resale Value Breakdown - Comparison</h4>
              <div className="comparison-grid">
                {/* Car A */}
                <div>
                  <h5>{data.scenarioName || 'Car A'}</h5>
                  <div className="resale-details">
                    <div className="resale-item">
                      <span className="resale-label">Year {data.ownershipTimeframe}:</span>
                      <span className="resale-value">{data.ownershipTimeframe}</span>
                    </div>
                    {(data.openingMileage ?? 0) > 0 && (
                      <div className="resale-item">
                        <span className="resale-label">Opening Odometer:</span>
                        <span className="resale-value">{data.openingMileage?.toLocaleString()} {data.region === 'Canada' ? 'km' : 'miles'}</span>
                      </div>
                    )}
                    <div className="resale-item">
                      <span className="resale-label">{(data.openingMileage ?? 0) > 0 ? 'Kilometers Driven:' : 'Total KM:'}</span>
                      <span className="resale-value">{calculateTotalDrivenKm(data).toLocaleString()} {data.region === 'Canada' ? 'km' : 'miles'}</span>
                    </div>
                    {(data.openingMileage ?? 0) > 0 && (
                      <div className="resale-item">
                        <span className="resale-label">Final Odometer:</span>
                        <span className="resale-value">{calculateFinalOdometer(data).toLocaleString()} {data.region === 'Canada' ? 'km' : 'miles'}</span>
                      </div>
                    )}
                    <div className="resale-item">
                      <span className="resale-label">Resale Value:</span>
                      <span className="resale-value">{formatCurrency(getExpectedValueAtEnd(data))}</span>
                    </div>
                    <div className="resale-item">
                      <span className="resale-label">Less Fees:</span>
                      <span className="resale-value">-{formatCurrency(getSellingCosts(data))}</span>
                    </div>
                    <div className="resale-item total">
                      <span className="resale-label">Estimated Net Sales Proceeds:</span>
                      <span className="resale-value">{formatCurrency(getExpectedValueAtEnd(data) - getSellingCosts(data))}</span>
                    </div>
                  </div>
                </div>
                {/* Car B */}
                <div>
                  <h5>{comparisonData!.scenarioName || 'Car B'}</h5>
                  <div className="resale-details">
                    <div className="resale-item">
                      <span className="resale-label">Year {comparisonData!.ownershipTimeframe}:</span>
                      <span className="resale-value">{comparisonData!.ownershipTimeframe}</span>
                    </div>
                    {(comparisonData!.openingMileage ?? 0) > 0 && (
                      <div className="resale-item">
                        <span className="resale-label">Opening Odometer:</span>
                        <span className="resale-value">{comparisonData!.openingMileage?.toLocaleString()} {comparisonData!.region === 'Canada' ? 'km' : 'miles'}</span>
                      </div>
                    )}
                    <div className="resale-item">
                      <span className="resale-label">{(comparisonData!.openingMileage ?? 0) > 0 ? 'Kilometers Driven:' : 'Total KM:'}</span>
                      <span className="resale-value">{calculateTotalDrivenKm(comparisonData!).toLocaleString()} {comparisonData!.region === 'Canada' ? 'km' : 'miles'}</span>
                    </div>
                    {(comparisonData!.openingMileage ?? 0) > 0 && (
                      <div className="resale-item">
                        <span className="resale-label">Final Odometer:</span>
                        <span className="resale-value">{calculateFinalOdometer(comparisonData!).toLocaleString()} {comparisonData!.region === 'Canada' ? 'km' : 'miles'}</span>
                      </div>
                    )}
                    <div className="resale-item">
                      <span className="resale-label">Resale Value:</span>
                      <span className="resale-value">{formatCurrency(getExpectedValueAtEnd(comparisonData!))}</span>
                    </div>
                    <div className="resale-item">
                      <span className="resale-label">Less Fees:</span>
                      <span className="resale-value">-{formatCurrency(getSellingCosts(comparisonData!))}</span>
                    </div>
                    <div className="resale-item total">
                      <span className="resale-label">Estimated Net Sales Proceeds:</span>
                      <span className="resale-value">{formatCurrency(getExpectedValueAtEnd(comparisonData!) - getSellingCosts(comparisonData!))}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h4>Resale Value Breakdown - {data.scenarioName || 'Car A'}</h4>
              <div className="resale-details">
                <div className="resale-item">
                  <span className="resale-label">Year {data.ownershipTimeframe}:</span>
                  <span className="resale-value">{data.ownershipTimeframe}</span>
                </div>
                {(data.openingMileage ?? 0) > 0 && (
                  <div className="resale-item">
                    <span className="resale-label">Opening Odometer:</span>
                    <span className="resale-value">{data.openingMileage?.toLocaleString()} {data.region === 'Canada' ? 'km' : 'miles'}</span>
                  </div>
                )}
                <div className="resale-item">
                  <span className="resale-label">{(data.openingMileage ?? 0) > 0 ? 'Kilometers Driven:' : 'Total KM:'}</span>
                  <span className="resale-value">{calculateTotalDrivenKm(data).toLocaleString()} {data.region === 'Canada' ? 'km' : 'miles'}</span>
                </div>
                {(data.openingMileage ?? 0) > 0 && (
                  <div className="resale-item">
                    <span className="resale-label">Final Odometer:</span>
                    <span className="resale-value">{calculateFinalOdometer(data).toLocaleString()} {data.region === 'Canada' ? 'km' : 'miles'}</span>
                  </div>
                )}
                <div className="resale-item">
                  <span className="resale-label">Resale Value:</span>
                  <span className="resale-value">{formatCurrency(getExpectedValueAtEnd(data))}</span>
                </div>
                <div className="resale-item">
                  <span className="resale-label">Less Fees:</span>
                  <span className="resale-value">-{formatCurrency(getSellingCosts(data))}</span>
                </div>
                <div className="resale-item total">
                  <span className="resale-label">Estimated Net Sales Proceeds:</span>
                  <span className="resale-value">{formatCurrency(getExpectedValueAtEnd(data) - getSellingCosts(data))}</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Lease Buyout Breakdown */}
      {activeAnalysisSection === 'lease-buyout' && (
        <div className="lease-buyout-breakdown">
          {isComparisonMode ? (
            <>
              <h4>Lease Buyout Breakdown - Comparison</h4>
              <div className="comparison-grid">
                {/* Car A */}
                <div>
                  <h5>{data.scenarioName || 'Car A'}</h5>
                  <div className="lease-buyout-details">
                    <div className="lease-buyout-item">
                      <span className="lease-buyout-label">Year {data.leaseTerm}:</span>
                      <span className="lease-buyout-value">{data.leaseTerm}</span>
                    </div>
                    <div className="lease-buyout-item">
                      <span className="lease-buyout-label">Lease Residual Value:</span>
                      <span className="lease-buyout-value">{formatCurrency(data.residualValue)}</span>
                    </div>
                    <div className="lease-buyout-item">
                      <span className="lease-buyout-label">Lease Buyout Fees:</span>
                      <span className="lease-buyout-value">{formatCurrency(data.leaseFees)}</span>
                    </div>
                    <div className="lease-buyout-item total">
                      <span className="lease-buyout-label">Total Lease Buyout Cost:</span>
                      <span className="lease-buyout-value">{formatCurrency(data.residualValue + data.leaseFees)}</span>
                    </div>
                  </div>
                </div>
                {/* Car B */}
                <div>
                  <h5>{comparisonData!.scenarioName || 'Car B'}</h5>
                  <div className="lease-buyout-details">
                    <div className="lease-buyout-item">
                      <span className="lease-buyout-label">Year {comparisonData!.leaseTerm}:</span>
                      <span className="lease-buyout-value">{comparisonData!.leaseTerm}</span>
                    </div>
                    <div className="lease-buyout-item">
                      <span className="lease-buyout-label">Lease Residual Value:</span>
                      <span className="lease-buyout-value">{formatCurrency(comparisonData!.residualValue)}</span>
                    </div>
                    <div className="lease-buyout-item">
                      <span className="lease-buyout-label">Lease Buyout Fees:</span>
                      <span className="lease-buyout-value">{formatCurrency(comparisonData!.leaseFees)}</span>
                    </div>
                    <div className="lease-buyout-item total">
                      <span className="lease-buyout-label">Total Lease Buyout Cost:</span>
                      <span className="lease-buyout-value">{formatCurrency(comparisonData!.residualValue + comparisonData!.leaseFees)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h4>Lease Buyout Breakdown - {data.scenarioName || 'Car A'}</h4>
              <div className="lease-buyout-details">
                <div className="lease-buyout-item">
                  <span className="lease-buyout-label">Year {data.leaseTerm}:</span>
                  <span className="lease-buyout-value">{data.leaseTerm}</span>
                </div>
                <div className="lease-buyout-item">
                  <span className="lease-buyout-label">Lease Residual Value:</span>
                  <span className="lease-buyout-value">{formatCurrency(data.residualValue)}</span>
                </div>
                <div className="lease-buyout-item">
                  <span className="lease-buyout-label">Lease Buyout Fees:</span>
                  <span className="lease-buyout-value">{formatCurrency(data.leaseFees)}</span>
                </div>
                <div className="lease-buyout-item total">
                  <span className="lease-buyout-label">Total Lease Buyout Cost:</span>
                  <span className="lease-buyout-value">{formatCurrency(data.residualValue + data.leaseFees)}</span>
                </div>
              </div>
            </>
          )}
        </div>
      )}


      {/* Annual Cost Graph */}
      {activeAnalysisSection === 'annual-cost-graph' && (
        <div className="annual-cost-graph-section">
          {isComparisonMode ? (
            <>
              <h4>Annual Cost Breakdown - Comparison</h4>
              <div className="scenario-selector">
                <label>Select Scenario:</label>
                <select
                  value={selectedBreakdownScenario}
                  onChange={(e) => setSelectedBreakdownScenario(e.target.value as BreakdownScenario)}
                >
                  <option value="buy">Buy</option>
                  {TylerState.car.includeFinanceScenario && (
                  <option value="finance">Finance</option>
                  )}
                  {TylerState.car.includeLeaseScenario && (
                  <option value="lease">Lease</option>
                  )}
                </select>
              </div>
              <div className="comparison-grid">
                {/* Car A */}
                <div>
                  <h5>{data.scenarioName || 'Car A'}</h5>
                  <div className="annual-cost-chart">
                    {(() => {
                      const results = calculateResults(data);
                      const scenarioData = results[selectedBreakdownScenario];
                      const yearlyData = scenarioData.yearlyData as any[];

                      // Calculate annual costs (change in closing balance)
                      let totalCost = 0;
                      const annualCosts = yearlyData.map((year, index) => {
                        totalCost += Math.max(0,year.openingBalance - year.closingBalance);
                        if (index === 0){
                          return year.openingBalance - year.closingBalance;
                        } else {
                          return yearlyData[index - 1].closingBalance - year.closingBalance;
                        }
                      });
                      totalCost = totalCost * 1.1;

                      return (
                        <div className="chart-container">
                          <div className="chart-bars">
                            {yearlyData.map((year, index) => (
                              <div key={index} className="chart-bar-container">
                                <div className="chart-bar-wrapper">
                                  <div
                                    className="chart-bar"
                                    style={{
                                      height: `${(Math.abs(year.closingBalance - year.openingBalance) / totalCost) * 200}px`,
                                      backgroundColor: year.closingBalance - year.openingBalance < 0 ? '#059669' : '#dc2626',
                                      bottom: year.closingBalance - year.openingBalance < 0 ? `${(Math.abs(year.openingBalance) / totalCost) * 200}px`: `${(Math.abs(year.closingBalance) / totalCost) * 200}px`
                                    }}
                                  />
                                  <div className="chart-bar-value">
                                    {formatCurrency(Math.abs(year.closingBalance - year.openingBalance))}
                                  </div>
                                </div>
                                <div className="chart-bar-label">
                                  Year {index + 1}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="chart-summary">
                            <p><strong>Total Cost:</strong> {formatCurrency(Math.abs(annualCosts.reduce((sum, cost) => sum + cost, 0)))}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
                {/* Car B */}
                <div>
                  <h5>{comparisonData!.scenarioName || 'Car B'}</h5>
                  <div className="annual-cost-chart">
                    {(() => {
                      const results = calculateResults(comparisonData!);
                      const scenarioData = results[selectedBreakdownScenario];
                      const yearlyData = scenarioData.yearlyData as any[];

                      // Calculate annual costs (change in closing balance)
                      // Calculate annual costs (change in closing balance)
                      let totalCost = 0;
                      const annualCosts = yearlyData.map((year, index) => {
                        totalCost += Math.max(0,year.openingBalance - year.closingBalance);
                        if (index === 0){
                          return year.openingBalance - year.closingBalance;
                        } else {
                          return yearlyData[index - 1].closingBalance - year.closingBalance;
                        }
                      });
                      totalCost = totalCost * 1.1;

                      return (
                        <div className="chart-container">
                          <div className="chart-bars">
                            {yearlyData.map((year, index) => (
                              <div key={index} className="chart-bar-container">
                                <div className="chart-bar-wrapper">
                                  <div
                                    className="chart-bar"
                                    style={{
                                      height: `${(Math.abs(year.closingBalance - year.openingBalance) / totalCost) * 200}px`,
                                      backgroundColor: year.closingBalance - year.openingBalance < 0 ? '#059669' : '#dc2626',
                                      bottom: year.closingBalance - year.openingBalance < 0 ? `${(Math.abs(year.openingBalance) / totalCost) * 200}px`: `${(Math.abs(year.closingBalance) / totalCost) * 200}px`
                                    }}
                                  />
                                  <div className="chart-bar-value">
                                    {formatCurrency(Math.abs(year.closingBalance - year.openingBalance))}
                                  </div>
                                </div>
                                <div className="chart-bar-label">
                                  Year {index + 1}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div className="chart-summary">
                            <p><strong>Total Cost:</strong> {formatCurrency(Math.abs(annualCosts.reduce((sum, cost) => sum + cost, 0)))}</p>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h4>Annual Cost Breakdown</h4>
              <div className="scenario-selector">
                <label>Select Scenario:</label>
                <select
                  value={selectedBreakdownScenario}
                  onChange={(e) => setSelectedBreakdownScenario(e.target.value as BreakdownScenario)}
                >
                  <option value="buy">Buy</option>
                  {TylerState.car.includeFinanceScenario && (
                  <option value="finance">Finance</option>
                  )}
                  {TylerState.car.includeLeaseScenario && (
                  <option value="lease">Lease</option>
                  )}
                </select>
              </div>
              <div className="annual-cost-chart">
                {(() => {
                  const results = calculateResults(data);
                  const scenarioData = results[selectedBreakdownScenario];
                  const yearlyData = scenarioData.yearlyData as any[];

                  // Calculate annual costs (change in closing balance)
                  let totalCost = 0;
                  const annualCosts = yearlyData.map((year, index) => {
                    totalCost += Math.max(0,year.openingBalance - year.closingBalance);
                    if (index === 0){
                      return year.openingBalance - year.closingBalance;
                    } else {
                      return yearlyData[index - 1].closingBalance - year.closingBalance;
                    }
                  });
                  totalCost = totalCost * 1.1;

                  return (
                    <div className="chart-container">
                      <div className="chart-bars">
                        {yearlyData.map((year, index) => (
                          <div key={index} className="chart-bar-container">
                            <div className="chart-bar-wrapper">
                              <div
                                className="chart-bar"
                                style={{
                                      height: `${(Math.abs(year.closingBalance - year.openingBalance) / totalCost) * 200}px`,
                                      backgroundColor: year.closingBalance - year.openingBalance < 0 ? '#27ae60' : '#e74c3c',
                                      bottom: year.closingBalance - year.openingBalance < 0 ? `${(Math.abs(year.openingBalance) / totalCost) * 200}px`: `${(Math.abs(year.closingBalance) / totalCost) * 200}px`
                                    }}
                                  />
                                  <div className="chart-bar-value">
                                    {formatCurrency(Math.abs(year.closingBalance - year.openingBalance))}
                              </div>
                            </div>
                            <div className="chart-bar-label">
                              Year {index + 1}
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="chart-summary">
                        <p><strong>Total Cost:</strong> {formatCurrency(Math.abs(annualCosts.reduce((sum, cost) => sum + cost, 0)))}</p>
                        <p><em>This chart shows the annual cost for each year, which equals total cost of ownership.</em></p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      )}

      {/* Cost of Ownership Breakdown */}
      {activeAnalysisSection === 'cost-of-ownership' && (
        <div className="cost-of-ownership-breakdown">
          {isComparisonMode ? (
            <>
              <h4>Cost of Ownership Breakdown - Comparison</h4>
              <div className="breakdown-selectors">
                <div className="breakdown-selector">
                  <label>Scenario:</label>
                  <select
                    value={selectedBreakdownScenario}
                    onChange={(e) => setSelectedBreakdownScenario(e.target.value as BreakdownScenario)}
                  >
                    <option value="buy">Buy</option>
                    {TylerState.car.includeFinanceScenario && (
                    <option value="finance">Finance</option>
                    )}
                    {TylerState.car.includeLeaseScenario && (
                    <option value="lease">Lease</option>
                    )}
                  </select>
                </div>
                <div className="breakdown-selector">
                  <label>Period:</label>
                  <select
                    value={selectedBreakdownPeriod}
                    onChange={(e) => setSelectedBreakdownPeriod(e.target.value as BreakdownPeriod)}
                  >
                    <option value="monthly">Monthly Average</option>
                    <option value="annual">Annual Average</option>
                    <option value="total">Total</option>
                    <option value="per-km">Cost per Kilometer</option>
                  </select>
                </div>
              </div>
              <div className="comparison-grid">
                <div>
                  <h5 style={{ marginBottom: '1rem' }}>{data.scenarioName || 'Car A'}</h5>
                  <div className="breakdown-display">
                    {renderCostBreakdown(selectedBreakdownScenario, selectedBreakdownPeriod, data)}
                  </div>
                </div>
                <div>
                  <h5 style={{ marginBottom: '1rem' }}>{comparisonData!.scenarioName || 'Car B'}</h5>
                  <div className="breakdown-display">
                    {renderCostBreakdown(selectedBreakdownScenario, selectedBreakdownPeriod, comparisonData!)}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h4>Cost of Ownership Breakdown - {data.scenarioName || 'Car A'}</h4>
              <div className="breakdown-selectors">
                <div className="breakdown-selector">
                  <label>Scenario:</label>
                  <select
                    value={selectedBreakdownScenario}
                    onChange={(e) => setSelectedBreakdownScenario(e.target.value as BreakdownScenario)}
                  >
                    <option value="buy">Buy</option>
                    {TylerState.car.includeFinanceScenario && (
                    <option value="finance">Finance</option>
                    )}
                    {TylerState.car.includeLeaseScenario && (
                    <option value="lease">Lease</option>
                    )}
                  </select>
                </div>
                <div className="breakdown-selector">
                  <label>Period:</label>
                  <select
                    value={selectedBreakdownPeriod}
                    onChange={(e) => setSelectedBreakdownPeriod(e.target.value as BreakdownPeriod)}
                  >
                    <option value="monthly">Monthly Average</option>
                    <option value="annual">Annual Average</option>
                    <option value="total">Total</option>
                    <option value="per-km">Cost per Kilometer</option>
                  </select>
                </div>
              </div>
              <div className="breakdown-display">
                {renderCostBreakdown(selectedBreakdownScenario, selectedBreakdownPeriod)}
              </div>
            </>
          )}
        </div>
      )}

      {/* Behind the Scenes Calculations */}
      {activeAnalysisSection === 'cash-flows' && (
        <div className="detailed-cash-flows-section">
          {isComparisonMode ? (
            <>
              <h4>Detailed Cash Flows - Comparison</h4>
              <div className="detailed-cash-flows-content">
                {/* Car A Tables */}
                <div style={{ marginBottom: '3rem' }}>
                  <h5 style={{ marginBottom: '1rem', fontSize: '1.25rem', borderBottom: '2px solid #3b82f6', paddingBottom: '0.5rem' }}>
                    {data.scenarioName || 'Car A'}
                  </h5>
                  <div className="cash-flow-tables">
              {/* Buy Scenario Table */}
              <div className="cash-flow-table">
                <h5>Purchase Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Cash Purchase</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(data).buy.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(yearData.cashPurchase)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(data).buy.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.cashPurchase, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(data).buy.yearlyData[calculateResults(data).buy.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Finance Scenario Table */}
              {TylerState.car.includeFinanceScenario && (
              <div className="cash-flow-table">
                <h5>Finance Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Down Payment</th><th>Monthly Finance</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(data).finance.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(index === 0 ? -data.downPayment : 0)}</td>
                        <td>{formatCurrency(yearData.monthlyCosts)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(data).finance.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency(-data.downPayment)}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.monthlyCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(data).finance.yearlyData[calculateResults(data).finance.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}
              {/* Lease Scenario Table */}
              {TylerState.car.includeLeaseScenario && (
              <div className="cash-flow-table">
                <h5>Lease Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Lease Down Payment</th><th>Lease Fees</th><th>Monthly Lease</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Overage Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Lease Buyout Payment</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(data).lease.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(index === 0 ? -data.leaseDownPayment : 0)}</td>
                        <td>{formatCurrency(index === 0 ? -data.leaseFees : 0)}</td>
                        <td>{formatCurrency(yearData.monthlyCosts)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.overageCost)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.leaseBuyoutPayment)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(data).lease.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency(-data.leaseDownPayment)}</td>
                      <td>{formatCurrency(-data.leaseFees)}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.monthlyCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.overageCost, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.leaseBuyoutPayment, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(data).lease.yearlyData[calculateResults(data).lease.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}
            </div>
                </div>

                {/* Car B Tables */}
                <div>
                  <h5 style={{ marginBottom: '1rem', fontSize: '1.25rem', borderBottom: '2px solid #10b981', paddingBottom: '0.5rem' }}>
                    {comparisonData!.scenarioName || 'Car B'}
                  </h5>
                  <div className="cash-flow-tables">
              {/* Buy Scenario Table - Car B */}
              <div className="cash-flow-table">
                <h5>Purchase Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Cash Purchase</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(comparisonData!).buy.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(yearData.cashPurchase)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(comparisonData!).buy.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.cashPurchase, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(comparisonData!).buy.yearlyData[calculateResults(comparisonData!).buy.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Finance Scenario Table - Car B */}
              {TylerState.car.includeFinanceScenario && (
              <div className="cash-flow-table">
                <h5>Finance Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Down Payment</th><th>Monthly Finance</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(comparisonData!).finance.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(index === 0 ? -comparisonData!.downPayment : 0)}</td>
                        <td>{formatCurrency(yearData.monthlyCosts)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(comparisonData!).finance.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency(-comparisonData!.downPayment)}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.monthlyCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(comparisonData!).finance.yearlyData[calculateResults(comparisonData!).finance.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}
              {/* Lease Scenario Table - Car B */}
              {TylerState.car.includeLeaseScenario && (
              <div className="cash-flow-table">
                <h5>Lease Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Lease Down Payment</th><th>Lease Fees</th><th>Monthly Lease</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Overage Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Lease Buyout Payment</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(comparisonData!).lease.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(index === 0 ? -comparisonData!.leaseDownPayment : 0)}</td>
                        <td>{formatCurrency(index === 0 ? -comparisonData!.leaseFees : 0)}</td>
                        <td>{formatCurrency(yearData.monthlyCosts)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.overageCost)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.leaseBuyoutPayment)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(comparisonData!).lease.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency(-comparisonData!.leaseDownPayment)}</td>
                      <td>{formatCurrency(-comparisonData!.leaseFees)}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.monthlyCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.overageCost, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.leaseBuyoutPayment, 0))}</td>
                      <td>{formatCurrency((calculateResults(comparisonData!).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(comparisonData!).lease.yearlyData[calculateResults(comparisonData!).lease.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}
            </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <h4>Detailed Cash Flows - {data.scenarioName || 'Car A'}</h4>
              <div className="detailed-cash-flows-content">
                <div className="cash-flow-tables">
              {/* Buy Scenario Table */}
              <div className="cash-flow-table">
                <h5>Purchase Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Cash Purchase</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(data).buy.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(yearData.cashPurchase)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(data).buy.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.cashPurchase, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).buy.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(data).buy.yearlyData[calculateResults(data).buy.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {/* Finance Scenario Table */}
              {TylerState.car.includeFinanceScenario && (
              <div className="cash-flow-table">
                <h5>Finance Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Down Payment</th><th>Monthly Finance</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(data).finance.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(index === 0 ? -data.downPayment : 0)}</td>
                        <td>{formatCurrency(yearData.monthlyCosts)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(data).finance.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency(-data.downPayment)}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.monthlyCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).finance.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(data).finance.yearlyData[calculateResults(data).finance.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}
              {/* Lease Scenario Table */}
              {TylerState.car.includeLeaseScenario && (
              <div className="cash-flow-table">
                <h5>Lease Car</h5>
                <table className="detailed-cash-flows-table">
                  <thead><tr><th>Year</th><th>Opening Balance</th><th>Lease Down Payment</th><th>Lease Fees</th><th>Monthly Lease</th><th>Gas</th><th>Insurance</th><th>Maintenance</th><th>Other Costs</th><th>Overage Costs</th><th>Opportunity Cost<br/>(Investments)</th><th>Lease Buyout Payment</th><th>Sell Car</th><th>Closing Balance</th></tr></thead>
                  <tbody>
                    {calculateResults(data).lease.yearlyData.map((yearData: any, index: number) => (
                      <tr key={index}>
                        <td>{yearData.year}</td>
                        <td>{formatCurrency(yearData.openingBalance)}</td>
                        <td>{formatCurrency(index === 0 ? -data.leaseDownPayment : 0)}</td>
                        <td>{formatCurrency(index === 0 ? -data.leaseFees : 0)}</td>
                        <td>{formatCurrency(yearData.monthlyCosts)}</td>
                        <td>{formatCurrency(yearData.gas)}</td>
                        <td>{formatCurrency(yearData.insurance)}</td>
                        <td>{formatCurrency(yearData.maintenance)}</td>
                        <td>{formatCurrency(yearData.otherCosts)}</td>
                        <td>{formatCurrency(yearData.overageCost)}</td>
                        <td>{formatCurrency(yearData.investmentReturn)}</td>
                        <td>{formatCurrency(yearData.leaseBuyoutPayment)}</td>
                        <td>{formatCurrency(yearData.sellCar)}</td>
                        <td>{formatCurrency(yearData.closingBalance)}</td>
                      </tr>
                    ))}
                    <tr className="total-row">
                      <td><strong>Total</strong></td>
                      <td>{formatCurrency(calculateResults(data).lease.yearlyData[0]?.openingBalance || 0)}</td>
                      <td>{formatCurrency(-data.leaseDownPayment)}</td>
                      <td>{formatCurrency(-data.leaseFees)}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.monthlyCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.gas, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.insurance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.maintenance, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.otherCosts, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.overageCost, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.investmentReturn, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.leaseBuyoutPayment, 0))}</td>
                      <td>{formatCurrency((calculateResults(data).lease.yearlyData as any[]).reduce((sum: any, year: any) => sum + year.sellCar, 0))}</td>
                      <td>{formatCurrency(calculateResults(data).lease.yearlyData[calculateResults(data).lease.yearlyData.length - 1]?.closingBalance || 0)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              )}
            </div>
          </div>
            </>
          )}
        </div>
      )}
    </>
  );
};

export default DetailedAnalysis;